"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail } from "lucide-react";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";
import { useFreeShippingThreshold } from "@/packages/hooks/useFreeShippingThreshold";
import { useGeneralSettings } from "@/packages/context/GeneralSettingContext";
import { useCurrency } from "@/packages/context/CurrencyContext";

export default function AnnouncementBarV2() {
  const { colors, typography, spacing } = HEADER_V2_DESIGN_SYSTEM;
  const freeShippingThreshold = useFreeShippingThreshold();
  const { symbol, exchangeRate, code } = useCurrency();
  const { settings } = useGeneralSettings();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const supportPhone = (mounted && settings?.contact?.phone) || "";
  const supportEmail = (mounted && settings?.contact?.email) || "";

  const currSymbol = mounted ? (symbol || "$") : "$";
  const currCode = mounted ? (code?.toUpperCase() || "USD") : "USD";
  const currRate = mounted ? (exchangeRate || 1) : 1;

  const p10 = `${currSymbol}${(10 * currRate).toFixed(2)} ${currCode}`;
  const pShip = (mounted && freeShippingThreshold !== null)
    ? `${symbol}${freeShippingThreshold.toFixed(2)} ${code.toUpperCase()}`
    : "";

  const offerText = `🎉 Save 5% on all orders above ${p10} (Use code FLAT5)${pShip ? `    •    Free shipping on orders over ${pShip}` : ""}`;

  return (
    <div
      role="region"
      aria-label="Announcement Bar"
      className={`${colors.primaryBg} text-white ${typography.fontFamilySans} ${typography.size12} font-medium ${spacing.paddingYxs} w-full border-b ${colors.borderColor}`}
    >
      <div className={`${spacing.containerPadding} max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center`}>
        {/* Left Column: Phone Support */}
        <div className={`hidden md:flex items-center ${spacing.gapXs} text-white/90 min-w-[130px]`}>
          <Phone className="w-3.5 h-3.5 text-white/90" aria-hidden="true" />
          <a
            href={`tel:${supportPhone}`}
            aria-label={`Call customer support at ${supportPhone}`}
            className="hover:text-white/80 transition-colors duration-200"
          >
            {supportPhone}
          </a>
        </div>

        {/* Center Column: Marketing Offer (Scrolling Marquee) */}
        <div className="flex-1 w-full md:max-w-[850px] mx-2 overflow-hidden whitespace-nowrap py-1">
          <div className="animate-marquee inline-flex gap-28 hover:[animation-play-state:paused] cursor-pointer text-center tracking-wide uppercase text-white/100 text-[10px] md:text-[11px] font-bold">
            <span>{offerText}</span>
            <span>{offerText}</span>
          </div>
        </div>

        {/* Right Column: Email Support */}
        <div className={`hidden md:flex items-center ${spacing.gapXs} text-white/90 min-w-[160px] justify-end`}>
          <Mail className="w-3.5 h-3.5 text-white/90" aria-hidden="true" />
          <a
            href={`mailto:${supportEmail}`}
            aria-label={`Email customer support at ${supportEmail}`}
            className="hover:text-white/80 transition-colors duration-200"
          >
            {supportEmail}
          </a>
        </div>
      </div>
    </div>
  );
}
